import { AlignmentNavigationStatus } from '@technology/web3d-plugin-trimbim';
import {
  AlignmentNavigationCapability,
  PlanarCapability,
  StationsNavigationCapability,
} from '@trimble/web3d-capabilities';
import {
  StationPathIndexProvider,
  StationsCapabilityProvider,
} from '@trimble/web3d-providers';
import { CapabilityRegistryContainer } from '@trimble/web3d';
import { getActiveNavCap, normalizedPathId } from './sync-source-helpers';
import type { SyncPosition } from './sync-position.types';

/**
 * Returns the meaningful sync position from an alignment source pane:
 * the alignment tool's current point along the path, cloned so it is safe
 * to hold across await boundaries.  Returns null when the pane has no
 * active `AlignmentNavigationCapability` or the capability is active but
 * not yet navigating (no alignment picked yet) — the tool's
 * `currentAlignmentPoint` is only populated while
 * {@link AlignmentNavigationStatus.Navigating}.  Callers should abort
 * sync on null. Carries the path distance, and the mission when the path is
 * a mission path.
 */
export function getAlignmentSyncPosition(
  pane: CapabilityRegistryContainer
): SyncPosition | null {
  const navCap = getActiveNavCap(pane);
  if (!(navCap instanceof AlignmentNavigationCapability)) return null;
  if (navCap.status() !== AlignmentNavigationStatus.Navigating) return null;

  const position = navCap.currentAlignmentPoint.clone();
  const pathId = normalizedPathId(navCap.alignmentModelId());
  if (!pathId) return { position };
  return {
    position,
    pathId,
    pathDistance: navCap.distance(),
    missionId: pane.get(StationPathIndexProvider)()?.missionOfPath(pathId),
  };
}

/**
 * Returns the meaningful sync position from a station/image source pane:
 * the canonical station position (cloned), with a fallback to the viewer
 * camera position when no station is selected or no
 * StationsNavigationCapability is active. Returns null only when the pane
 * has no viewer.
 *
 * `ImageCapabilityBase` extends `StationsNavigationCapability`, so this one
 * helper covers plain stations + Panorama + Planar — the whole subtree.
 * A selected station adds its key, run, and its mission and path distance
 * when indexed.
 */
export function getStationSyncPosition(
  pane: CapabilityRegistryContainer
): SyncPosition | null {
  const viewer = pane.viewerWrapper()?.viewer;
  if (!viewer) return null;

  const cameraPosition = { position: viewer.camera.position.clone() };
  const navCap = getActiveNavCap(pane);
  if (!(navCap instanceof StationsNavigationCapability)) return cameraPosition;

  const stationPosition = navCap.currentStationPosition();
  if (!stationPosition) return cameraPosition;

  const position = stationPosition.clone();
  const stationIndex = navCap.selectedStationIndex();
  const station =
    stationIndex === null
      ? undefined
      : pane.get(StationsCapabilityProvider)()?.stationsInfo()[stationIndex];
  if (!station) return { position };

  const index = pane.get(StationPathIndexProvider)();
  const entry = index?.getById(station.stationKey);
  const runId = station.runId ?? undefined;
  return {
    position,
    stationKey: station.stationKey,
    runId,
    missionId:
      entry?.missionId ?? (runId ? index?.missionOfRun(runId) : undefined),
    pathId: entry?.pathId ?? undefined,
    pathDistance: entry?.pathDistance,
  };
}

/**
 * Whether a station/image source pane's camera orientation carries a
 * real-world direction worth propagating to other panes. Returns false for
 * `PlanarCapability` (camera is fixed top-down, unrelated to the 3D scene);
 * true for plain stations and Panorama.
 */
export function isStationOrientationMeaningful(
  pane: CapabilityRegistryContainer
): boolean {
  return !(getActiveNavCap(pane) instanceof PlanarCapability);
}
